import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Group, Panel, Separator, useDefaultLayout } from 'react-resizable-panels';
import { PaneControls, PaneGroup, PanesMenu, usePaneLayout } from '../../components/Panes';
import { OverflowTabs } from '../../components/OverflowTabs';
import { ArrowLeft, Bookmark, Check, CircleDot, Clapperboard, CloudOff, Copy, Eye, History, Info, LayoutGrid, Loader2, Redo2, Scissors, Share, Undo2 } from 'lucide-react';
import { browserSupport, dismissEditorNote, editorNoteDismissed, editorNoteNeeded } from '../../lib/browser-support';
import { MAX_GRAPHIC_TRACKS, TICKS_PER_SECOND, type Size, allAudioTracks, graphicTrackCount, graphicTrackOf, clipDuration, frameRateLabel, layoutVideo, markRange, ops, resolveAt, resolveTargets, sequenceDuration, snap, sourceVersionIds, ticksPerFrame, transitionBorrow, type EditResult, type EditTargets, type GraphicTemplateDTO, type GraphicTrack, type SourceRange, type TransitionKind } from '@prism/contracts';
import { api, errorMessage, type ApiError } from '../../api/client';
import { useDuplicateSequence, useGraphicsSettings, useHandoffs, useMe, useSequence, useSequenceSources, useTemplates, type SequenceDetail } from '../../api/hooks';
import type { CheckinDTO, HandoffDTO } from '@prism/contracts';
import { CheckInDialog } from '../../components/CheckIn';
import { newClipFor } from '../../graphics/ograf';
import { editorDefaultsFor } from '../../graphics/editorDefaults';
import { resolveGraphicDrop } from '../../lib/graphic-drag';
import { layoutStorage, usePref } from '../../lib/prefs';
import { Modal } from '../../components/Modal';
import { toast } from '../../components/Toasts';
import { relative } from '../../lib/format';
import { isTyping } from '../../lib/keys';
import { useSequenceEditor } from './useSequenceEditor';
import { RowMenu } from '../../components/RowMenu';
import { TAB_ID, useSequenceLock } from './useSequenceLock';
import { LockBanner, LockRequest } from './LockBanner';
import { Timeline, type Selection } from './Timeline';
import { NO_SELECTION, pruneSelection, selectAll, selectOnly, type TimelineSelection } from './selection';
import { commandMenu, runCommand, splitScope, type CommandEnv, type CommandId } from './editCommands';
import { isSilent, sourceLengths } from './audioPreview';
import type { ContextMenuEntry } from '../../components/ContextMenu';
import { ProgramViewer, type ProgramViewerEdit } from './ProgramViewer';
import { TrimView, createTrimPreviewStore } from './TrimView';
import { toolForKey, type TimelineTool } from './timelineTools';
import { SourceViewer, type SourceCue, type SourceViewerHandle } from './SourceViewer';
import { SHORTENED, matchFrameAt, planThreePoint, runThreePoint, type ThreePointKind } from './threePointEdit';
import { KeysTab } from './KeysTab';
import { sideTabOf } from './editorKeys';
import { Bin } from './Bin';
import { Inspector, SequenceSubtitles } from './Inspector';
import { ExportDialog, ExportList } from './ExportPanel';
import { SendToEditDialog } from '../../components/SendToEdit';
import { MulticamDialog } from './MulticamDialog';
import { SequenceDetails } from './SequenceDetails';
import { MetersPane } from './AudioMeter';
import { PreviewAudio } from './previewAudio';
import { createPlayheadClock } from './playheadClock';
import { RunWorkflowButton } from '../../components/RunWorkflow';
// 1.8.0: the rights pill, the Rights side tab and the toast on a withdrawn clip (only while rights are on).
import { SequenceRightsPill, SequenceRightsTab, useWithdrawnClipToast } from './RightsPanel';
import { useRightsSettings } from '../../api/hooks/rights';
import { FormatViewProvider, useFormatViewState } from './formatContext';
import { AddToCollectionButton } from '../../components/AddToCollection';
import { AssignButton } from '../../components/AssignTask';

/** The save state in words, also the tooltip where a narrow top bar shows only its icon (1.7.0). */
const SAVE_TITLES: Record<string, string> = { saved: 'Saved', pending: 'Unsaved changes', saving: 'Saving…', conflict: 'Changed elsewhere', error: 'Not saved', locked: 'Not saved' };

export function EditorPage() {
  const { id } = useParams<{ id: string }>();
  const q = useSequence(id);
  // Wait for this visit's fetch: the editor starts from the revision it saves on top of, never from an older cached copy.
  if (q.isLoading || (q.isFetching && !q.isFetchedAfterMount)) return <div className="empty"><Loader2 className="spin" color="var(--accent)" /></div>;
  if (q.isError || !q.data) {
    return <div className="empty"><div className="empty-inner"><h2>Sequence not found</h2><p>{(q.error as Error)?.message}</p><Link className="btn" to="/create">Back to sequences</Link></div></div>;
  }
  return <Editor key={q.data.id} initial={q.data} />;
}

function Editor({ initial }: { initial: SequenceDetail }) {
  const me = useMe().data!;
  // May edit sequences at all; this tab edits only while it holds the sequence's edit lock (0.97.0).
  const mayEdit = me.permissions.includes('sequence.edit');
  const canExport = me.permissions.includes('export.create');
  const onLocked = useRef<(e: ApiError) => void>(() => undefined);
  const ed = useSequenceEditor(initial.id, { document: initial.document, revision: initial.revision, name: initial.name, description: initial.description },
    { tabId: TAB_ID, onLocked: (e) => onLocked.current(e) });
  const { doc, apply } = ed;
  const revisionRef = useRef(ed.revision);
  revisionRef.current = ed.revision;
  const lock = useSequenceLock(initial.id, ed.name, mayEdit, {
    settle: ed.settle, isDirty: ed.isDirty, revision: () => revisionRef.current,
    reload: async () => {
      const latest = await api<SequenceDetail>(`/api/v1/sequences/${initial.id}`);
      if (latest.revision !== revisionRef.current || ed.isDirty()) ed.reset(latest.document, latest.revision, latest.name, latest.description);
      setUpdated({ at: latest.updatedAt, by: latest.updatedBy, revision: latest.revision });
    },
  });
  onLocked.current = lock.onLocked;
  const canEdit = mayEdit && lock.editing;
  const { setWritable } = ed;
  useEffect(() => setWritable(canEdit), [canEdit, setWritable]);
  const qc = useQueryClient();
  const sources = useSequenceSources(initial.id, ed.revision);
  const templates = useTemplates();
  const graphicsSettings = useGraphicsSettings();
  const [playhead, setPlayheadRaw] = useState(0);
  // The live playhead (0.96.0): the sequence viewer writes it on every frame of playback, the timeline reads it;
  // `playhead` above is the position while paused, so playback does not re-render the editor.
  const [clock] = useState(() => createPlayheadClock(0));
  const [audio] = useState(() => new PreviewAudio());
  useEffect(() => () => audio.close(), [audio]);
  const [playing, setPlaying] = useState(false);
  const [transitionKind, setTransitionKind] = usePref<TransitionKind>('editor.transition', 'dissolve');
  // 1.7.2: the timeline's tool (Select, Slip, Slide; not saved) and the slip or slide in progress, for the trim view.
  const [tool, setTool] = useState<TimelineTool>('select');
  const [trimPreview] = useState(createTrimPreviewStore);
  // 1.7.3: several items can be selected (selection.ts); `primary` is the one the Inspector shows.
  const [selectionRaw, setSelectionRaw] = useState<TimelineSelection>(NO_SELECTION);
  // 1.7.3: the Sync toggle (ripples move the other tracks too), and the graphics tracks hidden in the preview (view
  // only, per sequence; exports draw every track).
  const [sync, setSync] = usePref('editor.syncRipple', true);
  const [hiddenGfxAll, setHiddenGfxAll] = usePref<Record<string, number[]>>('editor.hiddenGfx', {});
  const hiddenGfx = useMemo(() => {
    const v = hiddenGfxAll && typeof hiddenGfxAll === 'object' ? hiddenGfxAll[initial.id] : undefined;
    return Array.isArray(v) ? v.filter((n) => Number.isInteger(n)) : [];
  }, [hiddenGfxAll, initial.id]);
  const setHiddenGfx = useCallback((v: number[]) => {
    const next = { ...(hiddenGfxAll && typeof hiddenGfxAll === 'object' ? hiddenGfxAll : {}) };
    if (v.length) next[initial.id] = v; else delete next[initial.id];
    setHiddenGfxAll(next);
  }, [hiddenGfxAll, setHiddenGfxAll, initial.id]);
  const [pickedAsset, setPickedAsset] = useState<string | null>(null);
  const [activeViewer, setActiveViewer] = useState<'source' | 'program'>('program');
  const [sideSaved, setSide] = usePref<string>('editor.side', 'inspector');
  const rightsSettings = useRightsSettings().data;
  const rightsOn = Boolean(rightsSettings?.enabled);
  const side = sideTabOf(sideSaved) === 'rights' && !rightsOn ? 'inspector' : sideTabOf(sideSaved);
  // 1.7.4: the source patch (Source V / Source A in the track headers), per sequence; read through resolveTargets.
  const [targetsPref, setTargetsPref] = usePref<EditTargets | null>(`editor.targets.${initial.id}`, null);
  const targets = useMemo(() => resolveTargets(doc, targetsPref), [doc, targetsPref]);
  // 1.7.4: the source viewer's marks at an edit, match frame's cue, and Reveal in bin.
  const sourceRef = useRef<SourceViewerHandle>(null);
  const [cue, setCue] = useState<SourceCue | null>(null);
  const [revealN, setRevealN] = useState(0);
  const navigate = useNavigate();
  const duplicateSequence = useDuplicateSequence();
  const [pps, setPps] = usePref('editor.zoom', 60);
  const [exporting, setExporting] = useState(false);
  // Send to edit (1.0.0-rc.6) needs export.create and asset.download (media leaves PRISM).
  const [sending, setSending] = useState(false);
  const canSend = canExport && me.permissions.includes('asset.download');
  // Sent to edit and not checked in yet (1.0.0-rc.7): a banner, with Check in. PRISM stays editable meanwhile.
  const handoffs = useHandoffs(initial.id).data?.items ?? [];
  const outForEdit = handoffs.find((h) => h.status === 'ready' && h.kind === 'sequence' && !h.checkedInAt && !h.cleanedAt && !h.folderCheck?.missing) ?? null;
  const [checkingIn, setCheckingIn] = useState<HandoffDTO | null>(null);
  const [bannerHidden, setBannerHidden] = useState<string | null>(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [multicamOpen, setMulticamOpen] = useState(false);
  // Only saved versions are listed; export revisions are in the Exports tab.
  const [versions, setVersions] = useState(() => initial.versions.filter((v) => v.kind === 'named'));
  // When the sequence was last saved: from the server, then from this editor's autosaves.
  const [updated, setUpdated] = useState({ at: initial.updatedAt, by: initial.updatedBy, revision: initial.revision });
  const main = useDefaultLayout({ id: 'prism-editor-main', storage: layoutStorage });

  useEffect(() => {
    if (ed.saveState === 'saved' && ed.revision !== updated.revision) setUpdated({ at: new Date().toISOString(), by: me.user.displayName, revision: ed.revision });
  }, [ed.saveState, ed.revision, updated.revision, me.user.displayName]);

  const setSelection = useCallback((s: TimelineSelection) => {
    setSelectionRaw(s);
    const one = s.items.length === 1 ? s.items[0]! : null;
    // 1.9.0: several cues (Shift-click in the Subtitles tab) keep the Subtitles tab open too.
    if (one?.kind === 'cue' || (s.items.length > 1 && s.items.every((r) => r.kind === 'cue'))) setSide('subtitles');
    else if (s.items.length) setSide('inspector');
  }, [setSide]);
  // Items an edit or an undo took away are no longer selected.
  const selection = useMemo(() => pruneSelection(doc, selectionRaw), [doc, selectionRaw]);
  /** The item the inspectors and the single-clip keys act on (1.7.3: the primary item). */
  const primary: Selection = selection.primary;
  /** One item from the inspectors (null: nothing). */
  const selectOne = useCallback((s: Selection) => setSelection(s ? selectOnly(doc, s, true) : NO_SELECTION), [setSelection, doc]);
  // 1.9.0: the cues selected (the Subtitles tab's Shift-click selects several), and ⌥P's playback of a cue.
  const cueIds = useMemo(() => selection.items.filter((r) => r.kind === 'cue').map((r) => r.id), [selection.items]);
  const setCueIds = useCallback((ids: string[]) => {
    const items = ids.map((id) => ({ kind: 'cue' as const, id }));
    setSelection(items.length ? { items, primary: items[items.length - 1]! } : NO_SELECTION);
  }, [setSelection]);
  const playRangeRef = useRef<((fromTicks: number, toTicks: number) => void) | null>(null);
  const total = sequenceDuration(doc);
  const setPlayhead = useCallback((t: number) => {
    const v = Math.max(0, Math.min(t, Math.max(total, 0)));
    clock.set(v);
    setPlayheadRaw(v);
  }, [total, clock]);
  const sourceMap = useMemo(() => new Map((sources.data?.items ?? []).map((s) => [s.versionId, s])), [sources.data]);
  useWithdrawnClipToast(doc, sourceMap);
  // 1.7.0: the sources' displayed picture sizes (from /sources), for the inspector's upscale badge.
  const sourceSizes = useMemo(() => {
    const m = new Map<string, Size>();
    for (const s of sources.data?.items ?? []) {
      if (s.width && s.height) m.set(s.versionId, { width: s.width, height: s.height });
    }
    return m;
  }, [sources.data]);
  // 1.7.0: the active format and the monitor's view, for everything in the editor (FormatViewProvider below).
  const formatView = useFormatViewState(initial.id, doc);
  const viewerEdit = useMemo<ProgramViewerEdit>(() => ({
    apply, seal: ed.seal, canEdit,
    selectedClipId: primary && (primary.kind === 'video' || primary.kind === 'layer') ? primary.id : null,
    select: (clipId: string) => setSelection(selectOnly(doc, { kind: (doc.layers ?? []).some((l) => l.clips.some((c) => c.id === clipId)) ? 'layer' : 'video', id: clipId })),
  }), [apply, ed.seal, canEdit, primary, setSelection, doc]);
  const durations = useMemo(() => new Map((sources.data?.items ?? []).map((s) => [s.versionId, s.durationTicks ?? s.live?.recordedTicks ?? 0])), [sources.data]);
  // 1.8.0: the lengths for crossfades across the cut and transitions that use extra media; silent sources.
  const lengths = useMemo(() => sourceLengths(sources.data?.items ?? []), [sources.data]);
  const silent = useCallback((versionId: string) => isSilent(sourceMap, versionId), [sourceMap]);
  // 1.8.1 browser policy: the editor is made for Chromium. Safari and Firefox get a one-time note; Safari also says
  // that a clip with a transparent (VP9 alpha) proxy shows its transparency only in Chrome and Edge.
  const browser = useMemo(() => browserSupport(), []);
  const [browserNote, setBrowserNote] = useState(() => editorNoteNeeded(browser) && !editorNoteDismissed());
  const alphaNote = useCallback((versionId: string) => {
    const s = sourceMap.get(versionId);
    return browser === 'safari' && Boolean(s?.hasAlpha) && s?.mediaKind === 'video';
  }, [browser, sourceMap]);

  // New sources need signed proxy URLs: refresh the source list when the set of versions changes.
  const versionKey = sourceVersionIds(doc).sort().join(',');
  useEffect(() => { void qc.invalidateQueries({ queryKey: ['sequence-sources', initial.id] }); }, [versionKey, qc, initial.id]);

  const insertAt = (r: SourceRange, where: 'append' | 'insert') => {
    const playhead = clock.get();
    const hit = resolveAt(doc, playhead);
    const index = where === 'append' ? doc.video.length : hit ? hit.placed.index : doc.video.length;
    apply((d) => ops.insertClip(d, r, index));
    const start = where === 'append' ? (layoutVideo(doc).at(-1)?.recordOut ?? 0) : hit?.placed.recordIn ?? total;
    setPlayhead(start);
    toast(`Added “${r.label}”`);
  };
  const addAudio = (r: SourceRange) => {
    const playhead = clock.get();
    const next = ops.addAudio(doc, r, playhead);
    if (next === doc) return;
    apply(next);
    // A2 clips do not overlap (0.96.0): where the playhead is taken, the clip goes to the nearest free spot.
    const at = next.audio.at(-1)!.recordInTicks;
    toast(at === snap(playhead, doc.frameRate) ? `Added “${r.label}” to A2` : `Added “${r.label}” to A2 at the nearest free spot`);
  };
  // Onto the lowest layer that is free for the clip's length at the playhead, or a new layer on top.
  const addLayer = (r: SourceRange) => {
    const playhead = clock.get();
    const len = r.sourceOutTicks - r.sourceInTicks;
    const free = (doc.layers ?? []).find((l) => l.clips.every((c) => c.recordInTicks >= playhead + len || c.recordInTicks + clipDuration(c) <= playhead));
    if (!free && (doc.layers ?? []).length >= 8) { toast('All 8 video tracks above V1 are in use at the playhead', 'error'); return; }
    // 1.8.0: its sound goes to an audio track, linked (V2 → A2), unless the source is silent or there is no room.
    const next = ops.addLayerClip(doc, r, playhead, free?.id, { soundTo: 'track' });
    apply(next);
    const soundMoved = allAudioTracks(next).reduce((n, t) => n + t.clips.length, 0) > allAudioTracks(doc).reduce((n, t) => n + t.clips.length, 0);
    toast(`Added “${r.label}” to ${free ? `V${doc.layers.indexOf(free) + 2}` : `V${(doc.layers ?? []).length + 2}`}${soundMoved ? ', its sound linked on an audio track' : r.hasAudio === false ? '' : ': there is no free audio track for the sound, it stays in the clip'}`);
  };
  // A new clip: renderAt templates get their manifest's length; OGraf graphics in + hold + out (the hold set in
  // Admin → System), a sting its own length, all on the sequence's frame grid (0.93.0).
  // 1.7.3: graphics on one track never overlap (ops.addGraphic moves one to the nearest free time on its track), so
  // the graphic stays at the time the user picked: on the wanted track when it is free there, else on another graphics
  // track free at that time, else on a new one (up to GFX 3); only when every track is taken does it go to the nearest
  // free time on the wanted track. Answers the track it went on (null when nothing was added).
  const addTemplate = (t: GraphicTemplateDTO, at = clock.get(), track: GraphicTrack = 0): GraphicTrack | null => {
    const clip = newClipFor(t, graphicsSettings.data?.defaultHoldSeconds ?? 4, doc.frameRate);
    const start = snap(Math.max(0, at), doc.frameRate);
    const len = snap(clip.durationTicks, doc.frameRate);
    const count = graphicTrackCount(doc);
    const free = (k: number) => doc.graphics.every((g) => graphicTrackOf(g) !== k || g.recordInTicks >= start + len || g.recordInTicks + g.durationTicks <= start);
    const tracks = [track, ...[0, 1, 2].filter((k) => k !== track && k < count)];
    const dest = (tracks.find(free) ?? (count < MAX_GRAPHIC_TRACKS ? count : track)) as GraphicTrack;
    const next = ops.addGraphic(doc, { templateId: t.id, templateVersion: t.version, recordInTicks: start, durationTicks: clip.durationTicks, data: clip.data, ...(dest ? { track: dest as 1 | 2 } : {}) });
    if (next === doc) return null;
    apply(next);
    return dest;
  };

  /* ------------------------------------------------ 1.7.3: the commands of a selection (keys, context menu, Inspector) */

  const reveal = useCallback((assetId: string) => { setPickedAsset(assetId); setActiveViewer('source'); setRevealN((n) => n + 1); }, []);

  /* ------------------------------------------------ 1.7.4: three-point editing, match frame, marks, Duplicate */

  /** Insert (,) or Overwrite (.): the source's marks, the sequence In and Out and the playhead, on the targeted tracks. */
  const threePoint = (kind: ThreePointKind) => {
    if (!canEdit) return;
    const plan = planThreePoint(doc, sourceRef.current?.marks() ?? null, clock.get(), targetsPref);
    if ('problem' in plan) { toast(plan.problem, 'error'); return; }
    let r: EditResult;
    try {
      r = runThreePoint(doc, plan, kind, sync ? 'sync' : 'v1');
    } catch (err) {
      toast(errorMessage(err), 'error');
      return;
    }
    if (r.doc === doc) { toast('Nothing was placed: turn on Source V or Source A on a track', 'error'); return; }
    ed.seal();
    apply(r.doc);
    ed.seal();
    if (r.placed.length) setSelection({ items: r.placed, primary: r.placed[0]! });
    setPlayhead(plan.edit.recordOutTicks);
    toast(plan.edit.shortened ? SHORTENED : `${kind === 'overwrite' ? 'Overwrote with' : 'Inserted'} “${plan.src.label}”`);
  };
  const threePointRef = useRef(threePoint);
  threePointRef.current = threePoint;

  /** Match frame (F): the clip under the playhead in the source viewer, at the same frame, with its In and Out. */
  const matchFrame = useCallback(() => {
    const m = matchFrameAt(doc, clock.get(), primary);
    if (!m) { toast('Put the playhead over a clip to match its frame', 'error'); return; }
    setPickedAsset(m.assetId);
    setActiveViewer('source');
    setCue({ assetId: m.assetId, sourceTicks: m.sourceTicks, inTicks: m.inTicks, outTicks: m.outTicks, n: Date.now() });
  }, [doc, clock, primary]);

  /** Duplicate: the saved sequence (pending edits saved first) as a new one, opened. */
  const duplicate = async () => {
    try {
      await ed.flush();
      const copy = await duplicateSequence(initial.id);
      toast(`Duplicated as “${copy.name}”`);
      navigate(`/create/${copy.id}`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };
  const env: CommandEnv = { doc, apply, seal: ed.seal, playhead: () => clock.get(), sync, canEdit, setSelection, reveal, lengths, silent };
  const envRef = useRef(env);
  envRef.current = env;
  const run = useCallback((id: CommandId, s?: TimelineSelection) => runCommand(envRef.current, id, s ?? selection), [selection]);
  const menuFor = useCallback((s: TimelineSelection): ContextMenuEntry[] => commandMenu(envRef.current, s).map((m) => (m === 'separator' ? m : {
    key: m.id, label: m.label, keys: m.keys, disabled: m.disabled, danger: m.danger, onSelect: () => runCommand(envRef.current, m.id, s),
  })), []);

  /**
   * A transition of the transition menu's kind (T, or the toolbar button) at the selected storyline clip's start, or
   * at the cut nearest the playhead; T on a cut that already has that transition removes it.
   */
  const addTransition = useCallback((kind: TransitionKind, toggle: boolean) => {
    const placed = layoutVideo(doc);
    const ph = clock.get();
    const sel = primary?.kind === 'video' ? placed.find((p) => p.clip.id === primary.id && p.index > 0) : undefined;
    const target = sel ?? [...placed.slice(1)].sort((a, b) => Math.abs(a.recordIn - ph) - Math.abs(b.recordIn - ph))[0];
    if (!target) return;
    // 1.8.0: with the lengths, the transition uses the clips' extra media; the toast says when it still shortens.
    if (toggle && target.transition?.kind === kind) apply((d) => ops.setTransition(d, target.clip.id, null, lengths));
    else {
      const next = ops.setTransition(doc, target.clip.id, { kind, durationTicks: target.transition?.ticks ?? TICKS_PER_SECOND }, lengths);
      apply(next);
      const b = transitionBorrow(next, target.clip.id);
      const prev = doc.video[target.index - 1];
      if (b?.recorded && b.overlapTicks > 0 && prev) {
        const side = b.tailTicks < b.headTicks ? `after ‘${prev.label}’` : `before ‘${target.clip.label}’`;
        toast(`Not enough media ${side}: the transition shortens the sequence by ${(b.overlapTicks / TICKS_PER_SECOND).toFixed(2)} s`);
      }
    }
    setSelection(selectOnly(doc, { kind: 'video', id: target.clip.id }));
  }, [doc, clock, primary, apply, setSelection, lengths]);

  const addMarker = useCallback((at: number) => {
    const id = `k${Date.now().toString(36)}`;
    const next = ops.addMarker(doc, at, undefined, id);
    if (next === doc) { toast('A sequence can have at most 500 markers', 'error'); return; }
    apply(next);
    setSelection(selectOnly(next, { kind: 'marker', id }));
  }, [doc, apply, setSelection]);

  // Editing shortcuts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e, 'input, textarea, select, [contenteditable]')) return;
      const mod = e.metaKey || e.ctrlKey;
      const key = e.key.toLowerCase();
      // 1.7.3: select all, clear the selection and copy work in a read-only sequence too. Text selected on the page
      // is copied as text instead.
      if (mod && !e.altKey && !e.shiftKey && key === 'a') { e.preventDefault(); setSelection(selectAll(doc)); return; }
      if (e.key === 'Escape' && !mod) { if (selection.items.length) setSelection(NO_SELECTION); return; }
      if (mod && !e.altKey && !e.shiftKey && key === 'c') {
        if (window.getSelection()?.toString() || !selection.items.length) return;
        e.preventDefault();
        run('copy');
        return;
      }
      if (mod && !canEdit) return;
      if (mod && key === 'z') { e.preventDefault(); if (e.shiftKey) ed.redo(); else ed.undo(); return; }
      if (mod && key === 'y') { e.preventDefault(); ed.redo(); return; }
      // 1.7.4: ⌘K splits every track at the playhead; ⇧⌘D crossfades into the selected clip.
      if (mod && !e.altKey && !e.shiftKey && key === 'k') { e.preventDefault(); const at = clock.get(); ed.seal(); apply((d) => ops.splitAll(d, at)); ed.seal(); return; }
      if (mod && e.shiftKey && !e.altKey && key === 'd') { e.preventDefault(); run('crossfade'); return; }
      // 1.7.3: cut, paste at the playhead (⇧: insert), link and unlink.
      if (mod && !e.altKey && !e.shiftKey && key === 'x') { if (selection.items.length) { e.preventDefault(); run('cut'); } return; }
      if (mod && !e.altKey && key === 'v') { e.preventDefault(); run(e.shiftKey ? 'paste-insert' : 'paste'); return; }
      if (mod && !e.altKey && key === 'l') { if (selection.items.length) { e.preventDefault(); run(e.shiftKey ? 'unlink' : 'link'); } return; }
      // 1.7.4: match frame (F) with the sequence viewer or the timeline active (in the source viewer F is full screen).
      if (!mod && !e.altKey && !e.shiftKey && key === 'f' && activeViewer !== 'source') { e.preventDefault(); matchFrame(); return; }
      // 1.7.4: ⇧I / ⇧O go to the sequence In / Out.
      if (!mod && !e.altKey && e.shiftKey && (key === 'i' || key === 'o') && activeViewer !== 'source') {
        const t = key === 'i' ? doc.marks?.inTicks : doc.marks?.outTicks;
        if (t !== undefined) { e.preventDefault(); setPlayhead(t); }
        return;
      }
      if (mod || !canEdit) return;
      // 1.7.4: the sequence In and Out (I, O; ⌥X clears both), with the sequence viewer or the timeline active. One
      // undo step per press. e.code for ⌥X: Option changes e.key on a Mac.
      if (activeViewer !== 'source' && !e.shiftKey) {
        if (!e.altKey && (key === 'i' || key === 'o')) {
          e.preventDefault();
          const at = clock.get();
          ed.seal();
          apply((d) => ops.setMarks(d, key === 'i' ? { inTicks: at } : { outTicks: at }));
          ed.seal();
          return;
        }
        if (e.altKey && e.code === 'KeyX') {
          e.preventDefault();
          if (doc.marks) { ed.seal(); apply((d) => ops.setMarks(d, { inTicks: null, outTicks: null })); ed.seal(); }
          return;
        }
      }
      // 1.7.2: Option+, / Option+. slip the selected clip a frame earlier or later in its source (with Shift ten); the
      // presses of one run (within 1.5 s) are one undo step. e.code: Option changes e.key on a Mac.
      if (e.altKey && (e.code === 'Comma' || e.code === 'Period')) {
        const clip = primary && (primary.kind === 'video' || primary.kind === 'layer' || primary.kind === 'audio')
          ? [...doc.video, ...allAudioTracks(doc).flatMap((t) => t.clips), ...(doc.layers ?? []).flatMap((l) => l.clips)].find((c) => c.id === primary.id) : undefined;
        if (!clip) return;
        e.preventDefault();
        const frames = (e.code === 'Period' ? 1 : -1) * (e.shiftKey ? 10 : 1);
        const id = clip.id;
        apply((d) => ops.slipClip(d, id, frames * ticksPerFrame(d.frameRate), durations.get(clip.versionId) ?? 0), `slip:${id}`);
        return;
      }
      // 1.7.4: , inserts and . overwrites (three-point, on the targeted tracks), from any viewer or the timeline.
      if (!e.altKey && !e.shiftKey && (e.code === 'Comma' || e.code === 'Period')) { e.preventDefault(); threePointRef.current(e.code === 'Comma' ? 'insert' : 'overwrite'); return; }
      // 1.7.4: S splits the selected clips under the playhead (or V1).
      if (e.key.toLowerCase() === 's' && !e.shiftKey && !e.altKey) { e.preventDefault(); const at = clock.get(); const scope = splitScope(doc, selection.items, at); apply((d) => ops.splitAt(d, at, scope)); }
      if (e.key.toLowerCase() === 't' && !e.shiftKey) { e.preventDefault(); addTransition(transitionKind, true); }
      // M adds a marker; in the source viewer M mutes the source instead.
      if (e.key.toLowerCase() === 'm' && !e.shiftKey && !e.altKey && activeViewer !== 'source') { e.preventDefault(); addMarker(clock.get()); }
      // 1.7.3: Delete lifts (a V1 clip leaves a gap); Shift+Delete ripples (with Sync, the other tracks too). A
      // selected gap closes.
      if ((e.key === 'Delete' || e.key === 'Backspace') && selection.items.length) {
        e.preventDefault();
        run(e.shiftKey ? 'ripple-delete' : 'delete');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ed, apply, selection, primary, canEdit, clock, addTransition, transitionKind, addMarker, activeViewer, setSelection, doc, durations, run, matchFrame, setPlayhead]);

  // 1.7.2: the timeline's tools, V Select, Y Slip, U Slide; Escape goes back to Select. Listened to before the app's
  // own shortcuts (capture), so that U in an editor that may edit picks Slide rather than Upload. 1.7.4: B Ripple,
  // ⇧B Roll, C Blade, A Select forward; none of them while a dialog is open (its own capture listener cannot stop
  // this one).
  useEffect(() => {
    if (!canEdit) return;
    const onKey = (e: KeyboardEvent) => {
      // 1.7.3: a menu (the timeline's context menu) keeps its own keys.
      if (isTyping(e, 'input, textarea, select, [contenteditable], [role="menu"]') || e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.querySelector('[aria-modal="true"]')) return;
      const t = toolForKey(e.key, e.shiftKey);
      if (t) {
        e.preventDefault();
        e.stopPropagation();
        setTool(t);
      } else if (e.key === 'Escape') setTool('select');
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [canEdit]);
  useEffect(() => { if (!canEdit) setTool('select'); }, [canEdit]);

  async function saveVersion() {
    const label = window.prompt('Name this version', `Version ${versions.length + 1}`);
    if (!label) return;
    await ed.flush();
    try {
      const v = await api<{ id: string; revision: number; label: string }>(`/api/v1/sequences/${initial.id}/versions`, { method: 'POST', json: { label } });
      setVersions([{ ...v, kind: 'named', createdAt: new Date().toISOString(), createdBy: me.user.displayName }, ...versions]);
      toast(`Saved “${label}”`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  async function restore(versionId: string, label: string) {
    await ed.flush();
    const res = await api<{ revision: number; document: typeof doc }>(`/api/v1/sequences/${initial.id}/versions/${versionId}/restore`, { method: 'POST', headers: { 'x-prism-tab': TAB_ID }, json: {} });
    ed.reset(res.document, res.revision);
    setVersionsOpen(false);
    toast(`Restored “${label}”`);
  }

  /** A check-in applied as a new version of this sequence: this tab shows it (it applied with this tab's edit lock). */
  async function onCheckedIn(c: CheckinDTO) {
    if (!c.result || c.result.asNewSequence || c.result.sequenceId !== initial.id) return;
    const latest = await api<SequenceDetail>(`/api/v1/sequences/${initial.id}`);
    ed.reset(latest.document, latest.revision, latest.name, latest.description);
    setVersions(latest.versions.filter((v) => v.kind === 'named'));
    toast(`Checked in: revision ${c.result.revision} is the edit from the NLE`);
  }

  async function reloadLatest() {
    const latest = await api<SequenceDetail>(`/api/v1/sequences/${initial.id}`);
    ed.reset(latest.document, latest.revision, latest.name, latest.description);
    toast('Loaded the latest saved revision');
  }

  const saveLabel = {
    saved: <><Check size={13} /> Saved</>,
    pending: <><CircleDot size={11} className="save-dot" /> Unsaved changes</>,
    saving: <><Loader2 size={13} className="spin" /> Saving…</>,
    conflict: <><CloudOff size={13} /> Changed elsewhere</>,
    error: <><CloudOff size={13} /> Not saved</>,
    locked: <><CloudOff size={13} /> Not saved</>,
  }[ed.saveState];

  const panes = usePaneLayout('editor', {
    top: [
      { id: 'bin', label: 'Bin', defaultSize: '20%', minSize: '200px', render: () => (
                <Bin onPick={(assetId) => { setPickedAsset(assetId); setActiveViewer('source'); }} onAddTemplate={(t) => addTemplate(t)} pickedId={pickedAsset} canEdit={canEdit}
                  format={editorDefaultsFor(doc)} reveal={revealN} />
      ) },
      { id: 'source', label: 'Source viewer', defaultSize: '40%', minSize: '280px', render: () => (
                <SourceViewer assetId={pickedAsset} active={activeViewer === 'source'} onActivate={() => setActiveViewer('source')} canEdit={canEdit}
                  onAppend={(r) => insertAt(r, 'append')} onInsert={() => threePoint('insert')} onOverwrite={() => threePoint('overwrite')} onAddAudio={addAudio} onAddLayer={addLayer}
                  handle={sourceRef} cue={cue} />
      ) },
      { id: 'program', label: 'Sequence viewer', defaultSize: '40%', minSize: '280px', render: () => (
                <ProgramViewer doc={doc} sources={sourceMap} templates={templates.data?.items ?? []} playhead={playhead} setPlayhead={setPlayhead}
                  active={activeViewer === 'program'} onActivate={() => setActiveViewer('program')} clock={clock} audio={audio} onPlayingChange={setPlaying} hiddenGfx={hiddenGfx}
                  onAddMarker={canEdit ? addMarker : undefined} edit={viewerEdit} overlay={<TrimView store={trimPreview} sources={sourceMap} />} playRangeRef={playRangeRef} />
      ) },
    ],
    bottom: [
      { id: 'timeline', label: 'Timeline', required: true, defaultSize: '62%', minSize: '400px', render: () => (
                <Timeline doc={doc} apply={apply} seal={ed.seal} playhead={playhead} setPlayhead={(t) => { setActiveViewer('program'); setPlayhead(t); }}
                  selection={selection} setSelection={setSelection} sourceDurations={durations} pps={pps} setPps={setPps} canEdit={canEdit} templates={templates.data?.items ?? []}
                  clock={clock} playing={playing} sources={sourceMap} transitionKind={transitionKind} setTransitionKind={setTransitionKind} onAddTransition={(k) => addTransition(k, false)}
                  tool={tool} setTool={setTool} trimPreview={trimPreview}
                  sync={sync} setSync={setSync} hiddenGfx={hiddenGfx} setHiddenGfx={setHiddenGfx} menuFor={menuFor} targets={targets} setTargets={setTargetsPref}
                  onDropGraphic={(drop, at, track) => {
                    const t = resolveGraphicDrop(drop, templates.data?.items ?? []);
                    if (!t) { toast('That graphic is not ready (or was deleted)', 'error'); return; }
                    const on = addTemplate(t, at, track);
                    if (on !== null) toast(`Added “${t.asset?.title ?? t.manifest.name}” to GFX ${on + 1}`);
                  }} />
      ) },
      { id: 'side', label: 'Inspector, subtitles, exports, details and keys', defaultSize: '28%', minSize: '260px', render: () => (
                <div className="pane raised">
                  <div className="tabs tab-row side-tabs" style={{ height: 38 }}>
                    <OverflowTabs className="tab-strip" wrapClassName="tab-strip-wrap" role="tablist" label="Editor side panels" activeKey={side} testId="side-tabs">
                      <button role="tab" className="tab" aria-selected={side === 'inspector'} onClick={() => setSide('inspector')}>Inspector</button>
                      <button role="tab" className="tab" aria-selected={side === 'subtitles'} onClick={() => setSide('subtitles')} data-testid="tab-subtitles">Subtitles{doc.subtitles?.cues.length ? <span className="count">{doc.subtitles.cues.length}</span> : null}</button>
                      <button role="tab" className="tab" aria-selected={side === 'exports'} onClick={() => setSide('exports')} data-testid="tab-exports">Exports</button>
                      <button role="tab" className="tab" aria-selected={side === 'details'} onClick={() => setSide('details')} data-testid="tab-details">Details</button>
                      <button role="tab" className="tab" aria-selected={side === 'keys'} onClick={() => setSide('keys')} data-testid="tab-keys">Keys</button>
                      {rightsOn && <button role="tab" className="tab" aria-selected={side === 'rights'} onClick={() => setSide('rights')} data-testid="tab-rights">Rights</button>}
                    </OverflowTabs>
                    <PaneControls />
                  </div>
                  <div className="pane-body" style={{ padding: 12 }}>
                    {side === 'inspector' && <Inspector doc={doc} apply={apply} seal={ed.seal} selection={primary} setSelection={selectOne} items={selection.items} onCommand={run} templates={templates.data?.items ?? []} playhead={playhead} setPlayhead={setPlayhead} canEdit={canEdit} sourceSizes={sourceSizes} lengths={lengths} silent={silent} alphaNote={alphaNote} />}
                    {side === 'subtitles' && <SequenceSubtitles doc={doc} apply={apply} seal={ed.seal} selection={primary} setSelection={selectOne} playhead={playhead} setPlayhead={setPlayhead} canEdit={canEdit}
                      cueIds={cueIds} setCueIds={setCueIds} now={clock.get} playRange={(a, b) => { setActiveViewer('program'); playRangeRef.current?.(a, b); }} />}
                    {side === 'exports' && (
                      <>
                        {/* Exports and hand-offs in one list (rc.9 fixes): Send to edit rows with their progress and Check in. */}
                        <ExportList sequenceId={initial.id} tabId={TAB_ID} doc={doc} onApplied={(c) => void onCheckedIn(c)} />
                      </>
                    )}
                    {side === 'keys' && <KeysTab />}
                    {side === 'rights' && <SequenceRightsTab doc={doc} sources={sources.data?.items ?? []} sequenceId={initial.id} />}
                    {side === 'details' && (
                      <SequenceDetails doc={doc} description={ed.description} onDescription={ed.redescribe} canEdit={canEdit}
                        owner={initial.createdBy} createdAt={initial.createdAt ?? null} updatedAt={updated.at} updatedBy={updated.by} apply={apply} seal={ed.seal}
                        onDuplicate={mayEdit ? () => void duplicate() : undefined} />
                    )}
                  </div>
                </div>
      ) },
      // 1.7.4: the meters in a pane of their own (the Audio tab before), so they stay in view while editing.
      { id: 'meters', label: 'Meters', defaultSize: '10%', minSize: '120px', render: () => <MetersPane audio={audio} /> },
    ],
  });
  // Until this tab knows whether it holds the edit lock, nothing can be edited: show the editor once it does.
  if (lock.mode === 'starting') return <div className="empty"><Loader2 className="spin" color="var(--accent)" /></div>;
  return (
    <FormatViewProvider value={formatView}>
      <header className="topbar editor-topbar">
        <Link to="/create" className="btn ghost icon" aria-label="Back to sequences"><ArrowLeft size={17} /></Link>
        <input className="title-input" value={ed.name} onChange={(e) => ed.rename(e.target.value)} onBlur={ed.seal} disabled={!canEdit} aria-label="Sequence name" data-testid="sequence-name" />
        <span className="pill" data-testid="sequence-format" title={formatView.formats.length > 1 ? `Also ${formatView.formats.slice(1).map((f) => `${f.label} ${f.width}×${f.height}`).join(', ')}` : undefined}><span className="pill-main">{doc.width}×{doc.height}</span><span className="pill-rate">{'\u00a0·\u00a0'}{frameRateLabel(doc.frameRate)} fps</span>{formatView.formats.length > 1 && <span className="pill-extra">{'\u00a0·\u00a0'}+{formatView.formats.length - 1}</span>}</span>
        <span className={`save-state ${ed.saveState}`} data-testid="save-state" data-state={ed.saveState} title={SAVE_TITLES[ed.saveState]}>{saveLabel}</span>
        {!canEdit && <span className="pill" data-testid="read-only"><Eye size={12} /> Read only</span>}
        {rightsOn && <SequenceRightsPill sources={sources.data?.items ?? []} policy={rightsSettings?.policy} onOpen={() => setSide('rights')} />}

        <span className="topbar-spacer" />
        {canEdit && (
          <>
            <button className="btn ghost icon" onClick={ed.undo} disabled={!ed.canUndo} aria-label="Undo" title="Undo (⌘Z)"><Undo2 size={16} /></button>
            <button className="btn ghost icon" onClick={ed.redo} disabled={!ed.canRedo} aria-label="Redo" title="Redo (⇧⌘Z)"><Redo2 size={16} /></button>
            <button className="btn ghost icon" onClick={() => { const at = clock.get(); const scope = splitScope(doc, selection.items, at); apply((d) => ops.splitAt(d, at, scope)); }} aria-label="Split at playhead" title="Split at playhead: the selected clips, or V1 (S; ⌘K splits every track)" data-testid="split"><Scissors size={16} /></button>
            <button className="btn ghost sm" onClick={() => void saveVersion()} aria-label="Save version" title="Save version"><Bookmark size={14} /> <span className="btn-label">Save version</span></button>
            <button className="btn ghost sm" onClick={() => setMulticamOpen(true)} data-testid="open-multicam" aria-label="Multicam" title="Multicam: line up several camera angles and cut between them"><LayoutGrid size={14} /> <span className="btn-label">Multicam</span></button>
          </>
        )}
        <PanesMenu layout={panes} />
        {mayEdit && <RowMenu label="More" testId="editor-more" items={[{ key: 'duplicate', label: <><Copy size={13} /> Duplicate sequence</>, onSelect: () => void duplicate() }]} />}
        <button className="btn ghost sm" onClick={() => setVersionsOpen(true)} data-testid="open-versions" aria-label="Versions" title="Versions"><History size={14} /> <span className="btn-label">Versions</span></button>
        <AddToCollectionButton subject="sequence" id={initial.id} name={ed.name} />
        <AssignButton subject={{ type: 'sequence', id: initial.id, name: ed.name }} />
        <RunWorkflowButton subject="sequence" id={initial.id} name={ed.name} disabled={ed.saveState !== 'saved'} />
        {canSend && <button className="btn ghost sm" onClick={() => setSending(true)} disabled={!doc.video.length && !doc.audio.length} data-testid="send-to-edit-button" aria-label="Send to edit" title="Send to edit: timelines and media for Premiere, Resolve, Final Cut or Avid"><Clapperboard size={14} /> <span className="btn-label">Send to edit…</span></button>}
        {canExport && <button className="btn primary" onClick={() => setExporting(true)} disabled={!doc.video.length} data-testid="export-button"><Share size={15} /> Export</button>}
      </header>
      <LockBanner mode={lock.mode} state={lock.state} lost={lock.lost} asked={lock.asked} mayEdit={mayEdit} mayTakeOver={me.permissions.includes('sequence.take_over')}
        onStart={() => void lock.startEditing()} onAsk={() => void lock.askToEdit()} onTakeOver={() => void lock.takeOver()} />
      <LockRequest state={lock.state} name={ed.name} onAnswer={(a) => void lock.answer(a)} />
      {browserNote && (
        <div className="banner info" role="status" style={{ margin: '8px 12px 0' }} data-testid="editor-browser-note"><Info size={14} /><div className="banner-body">
          <span>PRISM&apos;s editor is made for Chrome or Edge.</span>
        </div>
          <button className="btn ghost sm" onClick={() => { dismissEditorNote(); setBrowserNote(false); }} aria-label="Hide the browser note" data-testid="editor-browser-note-dismiss">Got it</button>
        </div>
      )}
      {outForEdit && bannerHidden !== outForEdit.id && (
        <div className="banner info" role="status" style={{ margin: '8px 12px 0' }} data-testid="sent-to-edit-banner"><Clapperboard size={14} /><div className="banner-body">
          <strong>Sent to edit {relative(outForEdit.finishedAt ?? outForEdit.createdAt)} on {outForEdit.location.name}</strong>
          <span>The edit there is not checked in yet. You can keep editing here; Check in shows what changed and warns about edits made here since it was sent.</span>
        </div>
          {mayEdit && <button className="btn sm" onClick={() => { void ed.flush(); setCheckingIn(outForEdit); }} data-testid="banner-checkin">Check in…</button>}
          <button className="btn ghost sm" onClick={() => setBannerHidden(outForEdit.id)} aria-label="Hide the Sent to edit note">Hide</button>
        </div>
      )}
      {checkingIn && <CheckInDialog handoff={checkingIn} tabId={TAB_ID} onClose={() => setCheckingIn(null)} onApplied={(c) => void onCheckedIn(c)} />}
      {ed.saveState === 'conflict' && (
        <div className="offline-bar" role="alert">
          <CloudOff size={14} /> Someone else saved this sequence. Your latest edits are kept here but not saved.
          <button className="btn sm" onClick={() => void reloadLatest()}>Load their version</button>
        </div>
      )}
      {ed.saveState === 'error' && <div className="offline-bar" role="alert"><CloudOff size={14} /> {ed.saveError} · retrying automatically (and on the next change)</div>}
      <div className="content editor-content">
        <Group orientation="vertical" className="panel-group" defaultLayout={main.defaultLayout} onLayoutChanged={main.onLayoutChanged}>
          <Panel id="top" defaultSize="58%" minSize="220px">
            <PaneGroup layout={panes} row="top" />
          </Panel>
          <Separator className="resize-handle horizontal" />
          <Panel id="bottom" minSize="200px">
            <PaneGroup layout={panes} row="bottom" />
          </Panel>
        </Group>
      </div>
      {multicamOpen && <MulticamDialog doc={doc} apply={apply} sources={sourceMap} playhead={playhead} onClose={() => setMulticamOpen(false)} />}
      {exporting && (
        <ExportDialog sequenceId={initial.id} name={ed.name} revision={ed.revision} doc={doc} sources={sources.data?.items ?? []} flush={ed.flush}
          onClose={() => { setExporting(false); setSide('exports'); }} />
      )}
      {sending && (
        <SendToEditDialog subject={{ kind: 'sequence', id: initial.id, name: ed.name, hasSubtitles: Boolean(doc.subtitles?.cues.length), flush: ed.flush, formats: formatView.formats, hasMarks: Boolean(markRange(doc)) }} revision={ed.revision}
          onClose={() => { setSending(false); setSide('exports'); }} />
      )}
      {versionsOpen && (
        <Modal title="Versions" onClose={() => setVersionsOpen(false)} actions={<button className="btn" onClick={() => setVersionsOpen(false)}>Close</button>}>
          {versions.length === 0 && <p className="muted" data-testid="versions-empty">No saved versions yet. Use Save version to keep a copy of the sequence you can go back to. Exports are listed in the Exports tab.</p>}
          <div className="steps">
            {versions.map((v) => (
              <div className="step" key={v.id} data-testid="version-row">
                <span className="step-icon succeeded"><Bookmark size={11} /></span>
                <div className="step-main"><span className="step-title">{v.label}</span><span className="step-sub">Revision {v.revision} · {relative(v.createdAt)}{v.createdBy ? ` · ${v.createdBy}` : ''}</span></div>
                {canEdit && <button className="btn ghost sm" onClick={() => void restore(v.id, v.label)}>Restore</button>}
              </div>
            ))}
          </div>
        </Modal>
      )}
    </FormatViewProvider>
  );
}
